import { FC, ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';
import dayjs from 'dayjs';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { Button, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';

import { Blockchain, TransactionStatus } from '../../../../enums';
import { downloadBlob } from '../../../../utils';
import { type InfoFieldChangeHandler } from '../../../../components';
import {
  getReportBlob,
  useGetCoinsQuery,
  useGetCountriesQuery,
  useGetTransitAddressesQuery,
} from '../../../../api';
import type { Report } from '../../../../types';

type ReportFiltersProps = {
  report: Report;
  isReportsLoading: boolean;
};

export const ReportFilters: FC<ReportFiltersProps> = (props) => {
  const { report, isReportsLoading } = props;
  const { t } = useTranslation();

  // api
  const { data: coinList, isLoading: isCoinsLoading } = useGetCoinsQuery();
  const { data: countries, isLoading: isCountriesLoading } = useGetCountriesQuery();
  const { data: transitAddresses, isLoading: isTransitAddressesLoading } =
    useGetTransitAddressesQuery();

  // variables
  const isLoading = isCoinsLoading || isCountriesLoading || isTransitAddressesLoading;

  // fields for forms logic
  const [fields, setFields] = useState<Record<string, string> | null>(null);
  const handleFields: InfoFieldChangeHandler = (e, notEvent, fieldName) => {
    // @ts-ignore
    const name = notEvent && fieldName ? fieldName : e.target.name;
    // @ts-ignore
    const value = notEvent ? e : e.target.value;

    // @ts-ignore
    setFields((prevState) => {
      if (!prevState) {
        return {
          [name]: value,
        };
      }

      return {
        ...prevState,
        [name]: value,
      };
    });
  };
  const resetFields = () => {
    setFields(null);
  };

  // resetting form fields, when report is changed
  useEffect(() => {
    resetFields();
  }, [report]);

  // validation for required fields
  const checkSubmitDisabling = () => {
    // if (!fields) return true;

    const requiredFields = report.filters
      .filter((report) => report.required)
      .map((report) => report.input_name);

    for (const field of requiredFields) {
      if (fields && (fields[field] === undefined || fields[field] === '')) {
        return true;
      }
    }

    return false;
  };

  // download handler
  const [isReportBlobLoading, setIsReportBlobLoading] = useState(false);
  const downloadReport = async () => {
    // if (!fields) return;

    const getDayMappedFields = (fields: Record<string, string> | null): Record<string, string> => {
      const mappedFields: Record<string, string> = {};

      for (const key in fields) {
        const fieldValue = fields[key];
        mappedFields[key] = dayjs.isDayjs(fieldValue)
          ? dayjs(fieldValue).format('DD.MM.YYYY')
          : fieldValue;
      }

      return mappedFields;
    };

    const args = {
      report_type: report.name,
      params: getDayMappedFields(fields),
    };

    try {
      setIsReportBlobLoading(true);
      const res = await getReportBlob(args);
      const blob = new Blob([res.data], { type: res.headers['content-type'] });
      downloadBlob(blob, report.name, 'csv');
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    } finally {
      setIsReportBlobLoading(false);
    }
  };

  // elements
  const getCommonField = (inputName: string, isRequired: boolean) => {
    const commonFieldsMap: Record<string, ReactElement> = {
      coin_code: (
        <TextField
          key="coin_code"
          select
          name="coin_code"
          value={fields?.coin_code ?? ''}
          onChange={(e) => handleFields(e)}
          label={
            isRequired
              ? `*${t('reports.common-fields.coin-code')}`
              : t('reports.common-fields.coin-code')
          }
          fullWidth
          disabled={isLoading}
        >
          {[
            <MenuItem key="none" value={undefined}>
              <em>{t('ui.select.empty-value')}</em>
            </MenuItem>,
            ...(coinList?.coins.map((coin) => (
              <MenuItem key={coin.id} value={coin.code}>
                {coin.name}
              </MenuItem>
            )) ?? []),
          ]}
        </TextField>
      ),
      to_coin: (
        <TextField
          key="to_coin"
          select
          name="to_coin"
          value={fields?.to_coin ?? ''}
          onChange={(e) => handleFields(e)}
          label={
            isRequired
              ? `*${t('reports.common-fields.to-code')}`
              : t('reports.common-fields.to-code')
          }
          fullWidth
          disabled={isLoading}
        >
          {[
            <MenuItem key="none" value={undefined}>
              <em>{t('ui.select.empty-value')}</em>
            </MenuItem>,
            ...(coinList?.coins.map((coin) => (
              <MenuItem key={coin.id} value={coin.code}>
                {coin.name}
              </MenuItem>
            )) ?? []),
          ]}
        </TextField>
      ),
      from_coin: (
        <TextField
          key="from_coin"
          select
          name="from_coin"
          value={fields?.from_coin ?? ''}
          onChange={(e) => handleFields(e)}
          label={
            isRequired
              ? `*${t('reports.common-fields.from-code')}`
              : t('reports.common-fields.from-code')
          }
          fullWidth
          disabled={isLoading}
        >
          {[
            <MenuItem key="none" value={undefined}>
              <em>{t('ui.select.empty-value')}</em>
            </MenuItem>,
            ...(coinList?.coins.map((coin) => (
              <MenuItem key={coin.id} value={coin.code}>
                {coin.name}
              </MenuItem>
            )) ?? []),
          ]}
        </TextField>
      ),
      coin: (
        <TextField
          key="coin"
          select
          name="coin"
          value={fields?.coin ?? ''}
          onChange={(e) => handleFields(e)}
          label={
            isRequired ? `*${t('reports.common-fields.coin')}` : t('reports.common-fields.coin')
          }
          fullWidth
          disabled={isLoading}
        >
          {[
            <MenuItem key="none" value={undefined}>
              <em>{t('ui.select.empty-value')}</em>
            </MenuItem>,
            ...(coinList?.coins.map((coin) => (
              <MenuItem key={coin.id} value={coin.id}>
                {coin.name}
              </MenuItem>
            )) ?? []),
          ]}
        </TextField>
      ),
      status: (
        <TextField
          key="status"
          select
          name="status"
          value={fields?.status ?? ''}
          onChange={(e) => handleFields(e)}
          label={
            isRequired ? `*${t('reports.common-fields.status')}` : t('reports.common-fields.status')
          }
          fullWidth
          disabled={isLoading}
        >
          {[
            <MenuItem key="none" value={undefined}>
              <em>{t('ui.select.empty-value')}</em>
            </MenuItem>,
            ...(Object.values(TransactionStatus).map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            )) ?? []),
          ]}
        </TextField>
      ),
      blockchain: (
        <TextField
          key="blockchain"
          select
          name="blockchain"
          value={fields?.blockchain ?? ''}
          onChange={(e) => handleFields(e)}
          label={
            isRequired
              ? `*${t('reports.common-fields.blockchain')}`
              : t('reports.common-fields.blockchain')
          }
          fullWidth
        >
          {[
            <MenuItem key="none" value={undefined}>
              <em>{t('ui.select.empty-value')}</em>
            </MenuItem>,
            ...Object.values(Blockchain).map((it) => (
              <MenuItem key={it} value={it}>
                {it}
              </MenuItem>
            )),
          ]}
        </TextField>
      ),
      country_code: (
        <TextField
          key="country_code"
          select
          name="country_code"
          value={fields?.country_code ?? ''}
          onChange={(e) => handleFields(e)}
          label={
            isRequired
              ? `*${t('reports.common-fields.country-code')}`
              : t('reports.common-fields.country-code')
          }
          fullWidth
          disabled={isLoading}
        >
          {[
            <MenuItem key="none" value={undefined}>
              <em>{t('ui.select.empty-value')}</em>
            </MenuItem>,
            ...(countries?.countries.map((country) => (
              <MenuItem key={country.alpha2code} value={country.alpha2code}>
                {country.name}
              </MenuItem>
            )) ?? []),
          ]}
        </TextField>
      ),
      transit_address: (
        <TextField
          key="transit_address"
          select
          name="transit_address"
          value={fields?.transit_address ?? ''}
          onChange={(e) => handleFields(e)}
          label={
            isRequired
              ? `*${t('reports.common-fields.transit_address')}`
              : t('reports.common-fields.country-code')
          }
          fullWidth
          disabled={isLoading}
        >
          {[
            <MenuItem key="none" value={undefined}>
              <em>{t('ui.select.empty-value')}</em>
            </MenuItem>,
            ...(transitAddresses?.addresses.map((address) => (
              <MenuItem key={address.blockchain_address} value={address.id}>
                {`${address.coin.name} (${address.blockchain_address})`}
              </MenuItem>
            )) ?? []),
          ]}
        </TextField>
      ),
    };

    return commonFieldsMap[inputName] ?? false;
  };

  const getFilterFields = () => {
    if (!report) return null;

    return report.filters.map((filter) => {
      const { input_name, name, type, required } = filter;
      const commonField = getCommonField(input_name, required);
      const isCommonField = Boolean(commonField);
      const isDateField = type === 'date';

      return isCommonField ? (
        commonField
      ) : isDateField ? (
        <LocalizationProvider key={input_name} dateAdapter={AdapterDayjs}>
          <DatePicker
            name={input_name}
            value={fields?.[input_name] ? dayjs(fields[input_name]) : null}
            onChange={(newValue) => handleFields(newValue, true, input_name)}
            format="DD.MM.YYYY"
            label={name}
            slotProps={{ textField: { fullWidth: true, variant: 'outlined' } }}
          />
        </LocalizationProvider>
      ) : (
        <TextField
          key={input_name}
          name={input_name}
          value={fields?.[input_name] ?? ''}
          onChange={(e) => handleFields(e)}
          label={required ? `*${name}` : name}
          fullWidth
        />
      );
    });
  };

  return (
    <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
      <Stack direction="column" spacing={2} maxWidth={360}>
        <Typography variant="body1">{t('reports.label.choose-filters')}</Typography>

        {getFilterFields()}

        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            onClick={() => downloadReport()}
            disabled={
              !report ||
              isReportsLoading ||
              isLoading ||
              isReportBlobLoading ||
              checkSubmitDisabling()
            }
            fullWidth
          >
            {t('reports.download-btn')}
          </Button>

          <Button variant="outlined" onClick={() => resetFields()} fullWidth>
            {t('blockchain-fee.reset-btn')}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
};
